import { ReactNode, useMemo } from "react";
import { Link } from "react-router-dom";
import { AiBlockNode, AiInlineNode, AiTableAlign, IAiListBlock, parseAiMarkdown } from "./aiMarkdownParser";
import styles from "./AiMarkdown.module.scss";

interface IAiMarkdownProps {
  text: string;
}

/**
 * Renders the assistant's Markdown as React elements only (see aiMarkdownParser). Never use
 * dangerouslySetInnerHTML here: the text comes from a model that reads user-supplied data.
 */
export const AiMarkdown = ({ text }: IAiMarkdownProps) => {
  const blocks = useMemo(() => parseAiMarkdown(text), [text]);

  return <div className={styles.root}>{blocks.map(renderBlock)}</div>;
};

const renderInline = (nodes: AiInlineNode[]): ReactNode[] =>
  nodes.map((node, index) => {
    switch (node.type) {
      case "text":
        return node.text;
      case "strong":
        return <strong key={index}>{renderInline(node.children)}</strong>;
      case "em":
        return <em key={index}>{renderInline(node.children)}</em>;
      case "del":
        return <del key={index}>{renderInline(node.children)}</del>;
      case "code":
        return (
          <code key={index} className={styles.inline_code}>
            {node.text}
          </code>
        );
      case "br":
        return <br key={index} />;
      case "link":
        return node.external ? (
          <a key={index} className={styles.link} href={node.href} target="_blank" rel="noopener noreferrer">
            {renderInline(node.children)}
          </a>
        ) : (
          <Link key={index} className={styles.link} to={node.href}>
            {renderInline(node.children)}
          </Link>
        );
      default:
        return null;
    }
  });

const alignClassName = (align: AiTableAlign | undefined): string | undefined =>
  align ? styles[`align_${align}`] : undefined;

const renderList = (list: IAiListBlock, key: number | string): ReactNode => {
  const items = list.items.map((item, index) => (
    <li key={index}>
      {renderInline(item.content)}
      {item.children.map((child, childIndex) => renderList(child, childIndex))}
    </li>
  ));

  return list.ordered ? (
    <ol key={key} className={styles.list} start={list.start !== 1 ? list.start : undefined}>
      {items}
    </ol>
  ) : (
    <ul key={key} className={styles.list}>
      {items}
    </ul>
  );
};

const renderBlock = (block: AiBlockNode, index: number): ReactNode => {
  switch (block.type) {
    case "paragraph":
      return (
        <p key={index} className={styles.paragraph}>
          {renderInline(block.content)}
        </p>
      );
    case "heading": {
      const Heading = `h${block.level}` as "h3" | "h4" | "h5";
      return (
        <Heading key={index} className={styles.heading}>
          {renderInline(block.content)}
        </Heading>
      );
    }
    case "code":
      return (
        <pre key={index} className={styles.code_block} data-language={block.language ?? undefined}>
          <code>{block.code}</code>
        </pre>
      );
    case "list":
      return renderList(block, index);
    case "quote":
      return (
        <blockquote key={index} className={styles.quote}>
          {block.children.map(renderBlock)}
        </blockquote>
      );
    case "table":
      return (
        <div key={index} className={styles.table_wrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                {block.header.map((cell, column) => (
                  <th key={column} className={alignClassName(block.align[column])}>
                    {renderInline(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, column) => (
                    <td key={column} className={alignClassName(block.align[column])}>
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "hr":
      return <hr key={index} className={styles.rule} />;
    default:
      return null;
  }
};
